<script setup lang="ts">
import { computed } from 'vue'
import type { PageDefinition } from '@/types/page'
import AppIcon from '@/components/AppIcon.vue'
import { formatMoney, formatNumber } from '@/utils/format'

const props = defineProps<{
  definition: PageDefinition
  summary: Record<string, number | string>
}>()

const icons = ['users', 'check-circle', 'heart', 'device', 'star', 'clock']
const tones = ['brand', 'info', 'danger', 'purple', 'warning', 'cyan']

const cards = computed(() => (props.definition.summary ?? []).map((item, index) => {
  const raw = props.summary[item.key] ?? 0
  const numeric = Number(raw)
  const value = item.kind === 'ratio'
    ? `${Number.isFinite(numeric) ? numeric.toFixed(item.precision ?? 1) : 0}${item.suffix ?? '%'}`
    : item.kind === 'money'
      ? formatMoney(numeric)
      : `${formatNumber(numeric)}${item.suffix ?? ''}`
  return {
    key: item.key,
    label: item.label,
    hint: item.hint ?? '',
    value,
    icon: icons[index % icons.length] ?? 'info',
    tone: tones[index % tones.length] ?? 'brand',
  }
}))
</script>

<template>
  <div class="summary-grid" :class="`summary-grid--${Math.min(cards.length, 6)}`">
    <article v-for="(card, index) in cards" :key="card.key" class="summary-card" :class="`summary-card--${card.tone}`">
      <div class="summary-card__top">
        <span class="summary-card__icon"><AppIcon :name="card.icon" :size="19" /></span>
        <span class="summary-card__label">{{ card.label }}</span>
        <span v-if="index === 0" class="summary-card__spark" aria-hidden="true">
          <svg viewBox="0 0 64 24" width="44" height="18" fill="none"><path d="M2 19c7 0 8-9 15-9s8 6 14 4 8-9 14-9 9 6 17 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" opacity=".35"/></svg>
        </span>
      </div>
      <strong class="summary-card__value">{{ card.value }}</strong>
      <p v-if="card.hint" class="summary-card__hint">{{ card.hint }}</p>
      <span class="summary-card__shape" aria-hidden="true"></span>
    </article>
  </div>
</template>

<style scoped>
.summary-grid { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 12px; margin-bottom: 16px; }
.summary-grid--5 { grid-template-columns: repeat(5, minmax(0, 1fr)); }
.summary-grid--4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.summary-grid--3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.summary-grid--2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.summary-grid--1 { grid-template-columns: minmax(0, 1fr); }
.summary-card {
  position: relative;
  min-width: 0;
  padding: 14px 14px 12px;
  border: 1px solid var(--c-border);
  border-radius: var(--radius-card);
  background: #fff;
  box-shadow: var(--shadow-card);
  overflow: hidden;
  min-height: 106px;
}
.summary-card__top { display: flex; align-items: center; gap: 8px; min-width: 0; padding-right: 42px; }
.summary-card__icon {
  width: 32px; height: 32px; border-radius: 9px; display: grid; place-items: center; flex: 0 0 auto;
  color: var(--sc, #077e58); background: var(--sb, var(--c-brand-soft));
}
.summary-card__label { font-size: 12px; color: #53657e; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.summary-card__spark { position: absolute; top: 13px; right: 11px; color: var(--sc, var(--c-brand)); opacity: .8; }
.summary-card__value { display: block; margin-top: 7px; font-family: var(--font-num); font-size: 26px; line-height: 1; letter-spacing: -.4px; color: var(--c-ink); font-variant-numeric: tabular-nums; }
.summary-card__hint { margin-top: 7px; font-size: 10.5px; color: var(--c-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.summary-card__shape {
  position: absolute; width: 58px; height: 58px; right: -16px; bottom: -22px; border-radius: 50%;
  background: var(--sb, var(--c-brand-soft)); opacity: .55;
}
.summary-card--brand { --sc: #067d57; --sb: #e5f8f1; }
.summary-card--info { --sc: #256fbf; --sb: #eaf3fe; }
.summary-card--danger { --sc: #d33a3a; --sb: #feecec; }
.summary-card--purple { --sc: #6c57cf; --sb: #f1eefe; }
.summary-card--warning { --sc: #ae7008; --sb: #fff5e1; }
.summary-card--cyan { --sc: #0f8493; --sb: #e8f9fb; }
@media (max-width: 1439px) { .summary-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 767px) { .summary-grid, .summary-grid--5, .summary-grid--4, .summary-grid--3 { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; } }
@media (max-width: 479px) { .summary-card { min-height: 98px; padding: 12px; } .summary-card__value { font-size: 23px; } }
</style>
